import {
  BoxProps,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  HStack,
  InputGroup,
  Input,
  InputLeftElement,
  InputRightElement,
  Text,
  Flex,
  Select,
  Checkbox,
  ResponsiveValue,
  Textarea,
  NumberInputField,
  NumberInput,
  VStack,
  IconButton,
  useColorModeValue,
} from '@chakra-ui/react';
import { FormikErrors, FormikProps, getIn, useFormikContext } from 'formik';
import _ from 'lodash';
import React, { useState } from 'react';

import { Maybe } from 'types/dashboard-admin-gqlTypes.generated';

import { colors } from '../colors';
import { Select as FinleySelect } from './Select';
import { Icon as FinleyIcon } from 'packages/common-frontend/components/icons';

// Helpers
export const getSingleErrorMessageFromFieldErrors = (
  formErrors:
    | string
    | string[]
    | FormikErrors<any>
    | FormikErrors<any>[]
    | undefined,
) => {
  // formErrors=undefined, discard
  if (typeof formErrors === 'undefined') return undefined;
  // formErrors=string, return single error message
  if (typeof formErrors === 'string') return formErrors;
  // formErrors=string[], return first error message, discard FormikErrors<any>[]
  if (Array.isArray(formErrors)) {
    return [...formErrors]
      .filter((e): e is string => typeof e === 'string')
      .pop();
  }
  // formErrors=FormikErrors<any>, discard
  return undefined;
};

// Exports
type FormFieldLabelProps = {
  label: string;
  sublabel?: string;
  labelWidth?: string | number;
  tooltip?: React.ReactNode;
};
export const FormFieldLabel = ({
  label,
  sublabel,
  labelWidth,
  tooltip,
}: FormFieldLabelProps) => {
  return (
    <VStack w={labelWidth}>
      <HStack w="100%">
        <FormLabel mb={sublabel ? '0' : '2'} mr={tooltip ? '8px' : undefined}>
          {label}
        </FormLabel>
        {tooltip}
      </HStack>
      {sublabel && (
        <FormLabel fontSize="sm" color="darkgray">
          {sublabel}
        </FormLabel>
      )}
    </VStack>
  );
};

type CheckboxFormFieldProps = {
  propName: string;
  formProps: FormikProps<any>;
  isRequired?: boolean;
  isDisabled?: boolean;
  flexDirection?: ResponsiveValue<any> | undefined;
  labelWidth?: string | number;
  alignItems?: string;
  readOnly?: boolean;
  hidden?: boolean;
  marginTop?: number;
  label?: string;
  sublabel?: string;
  inputWidth?: string | number;
  value: boolean;
};

export const CheckboxFormField = ({
  propName,
  formProps,
  isRequired,
  isDisabled,
  flexDirection = 'column',
  labelWidth,
  alignItems,
  readOnly,
  inputWidth,
  marginTop,
  hidden,
  label,
  sublabel,
  value,
}: CheckboxFormFieldProps) => {
  return (
    <FormControl
      marginTop={marginTop}
      isRequired={isRequired}
      isInvalid={Boolean(
        formProps.errors[propName] && formProps.touched[propName],
      )}
    >
      <Flex flexDirection={flexDirection} alignItems={alignItems}>
        {label && (
          <FormFieldLabel
            label={label}
            sublabel={sublabel}
            labelWidth={labelWidth}
          />
        )}
        <InputGroup width="100%">
          <Checkbox
            isDisabled={isDisabled}
            type="checkbox"
            data-testid={propName}
            name={propName}
            onChange={formProps.handleChange}
            onBlur={formProps.handleBlur}
            readOnly={readOnly}
            hidden={hidden}
            checked={value}
            w={inputWidth}
            defaultChecked={value}
          />
        </InputGroup>
      </Flex>
      <FormErrorMessage>
        {getSingleErrorMessageFromFieldErrors(formProps.errors[propName])}
      </FormErrorMessage>
    </FormControl>
  );
};

/* eslint-disable react/no-unused-prop-types */
interface InputFormFieldProps {
  propName: string;
  type?:
    | 'text'
    | 'dollar'
    | 'email'
    | 'password'
    | 'number'
    | 'date'
    | 'datetime-local'
    | 'formula';
  formProps: FormikProps<any>;
  handleChange?: (e: React.ChangeEvent<any>) => void;
  isRequired?: boolean;
  isDisabled?: boolean;
  value?: Maybe<string>;
  showHideButton?: boolean;
  readOnly?: boolean;
  flexDirection?: ResponsiveValue<any> | undefined;
  labelWidth?: string | number;
  alignItems?: string;
  hidden?: boolean;
  marginTop?: number;
  label?: string;
  sublabel?: string;
  placeholder?: string;
  inputWidth?: string | number;
  step?: string | undefined;
}
/* eslint-enable react/no-unused-prop-types */

interface InputFormFieldPropsWithSelectOptions extends InputFormFieldProps {
  selectOptions: { value: string; label?: string }[];
}

interface TextareaInputFormFieldProps extends InputFormFieldProps {
  inputHeight?: string;
  maxInputHeight?: string;
}

export const InputFormField = ({
  propName,
  type = 'text',
  formProps,
  isRequired,
  flexDirection = 'column',
  labelWidth,
  alignItems,
  readOnly,
  marginTop,
  value,
  hidden,
  label,
  sublabel,
  placeholder,
  inputWidth,
  step,
  isDisabled = false,
}: InputFormFieldProps) => {
  const inputType = type === 'dollar' ? 'number' : type;
  let PlaceholderIcon;
  if (type === 'dollar') {
    PlaceholderIcon = (
      <InputLeftElement
        pointerEvents="none"
        color="gray.300"
        fontSize="1.2em"
        height="100%"
      >
        $
      </InputLeftElement>
    );
  }
  if (type === 'formula') {
    PlaceholderIcon = (
      <InputLeftElement
        pointerEvents="none"
        color="gray.300"
        fontSize="1.2em"
        height="100%"
      >
        =
      </InputLeftElement>
    );
  }

  let sanitizedValue = value;
  if (!value && _.isNumber(value)) {
    sanitizedValue = `${value}`;
  }

  const [show, setShow] = React.useState(false);
  const handleClick = () => setShow(!show);

  return (
    <FormControl
      marginTop={marginTop}
      isRequired={isRequired}
      isInvalid={Boolean(
        getIn(formProps.errors, propName) && getIn(formProps.touched, propName),
      )}
    >
      <Flex flexDirection={flexDirection} alignItems={alignItems}>
        {label && (
          <FormFieldLabel
            label={label}
            sublabel={sublabel}
            labelWidth={labelWidth}
          />
        )}
        <InputGroup width="100%">
          {PlaceholderIcon}
          <Input
            type={inputType === 'password' && show ? 'text' : inputType}
            placeholder={placeholder}
            data-testid={propName}
            width={inputWidth}
            name={propName}
            value={sanitizedValue || getIn(formProps.values, propName) || ''}
            onChange={formProps.handleChange}
            onBlur={formProps.handleBlur}
            readOnly={readOnly}
            hidden={hidden}
            step={step}
            isDisabled={isDisabled}
          />
          {inputType === 'password' && (
            <InputRightElement>
              <IconButton
                aria-label="Show/hide password"
                icon={
                  show ? (
                    <FinleyIcon icon="eye" />
                  ) : (
                    <FinleyIcon icon="eye-slash" />
                  )
                }
                onClick={handleClick}
                variant="ghost"
              />
            </InputRightElement>
          )}
        </InputGroup>
      </Flex>
      <FormErrorMessage>{getIn(formProps.errors, propName)}</FormErrorMessage>
    </FormControl>
  );
};

export const NumberInputFormField = ({
  propName,
  formProps,
  isRequired,
  flexDirection = 'column',
  labelWidth,
  alignItems,
  readOnly,
  marginTop,
  value,
  hidden,
  label,
  sublabel,
  placeholder,
  inputWidth,
}: InputFormFieldProps) => {
  let formPropsValue = getIn(formProps.values, propName);
  if (!formPropsValue && _.isNumber(formPropsValue)) {
    formPropsValue = `${formPropsValue}`;
  }

  return (
    <FormControl
      marginTop={marginTop}
      isRequired={isRequired}
      isInvalid={Boolean(
        getIn(formProps.errors, propName) && getIn(formProps.touched, propName),
      )}
    >
      <Flex flexDirection={flexDirection} alignItems={alignItems}>
        {label && (
          <FormFieldLabel
            label={label}
            sublabel={sublabel}
            labelWidth={labelWidth}
          />
        )}
        <InputGroup width="100%">
          <NumberInput
            defaultValue={value || formPropsValue || ''}
            width="100%"
          >
            <NumberInputField
              placeholder={placeholder}
              data-testid={propName}
              width={inputWidth}
              name={propName}
              onChange={formProps.handleChange}
              onBlur={formProps.handleBlur}
              readOnly={readOnly}
              hidden={hidden}
              type="number"
            />
          </NumberInput>
        </InputGroup>
      </Flex>
      <FormErrorMessage>{getIn(formProps.errors, propName)}</FormErrorMessage>
    </FormControl>
  );
};

export const TextareaFormField = ({
  propName,
  formProps,
  isRequired,
  flexDirection = 'column',
  labelWidth,
  alignItems,
  readOnly,
  marginTop,
  value,
  hidden,
  label,
  sublabel,
  placeholder,
  inputWidth,
  inputHeight,
  maxInputHeight,
}: TextareaInputFormFieldProps) => {
  return (
    <FormControl
      marginTop={marginTop}
      isRequired={isRequired}
      isInvalid={Boolean(
        getIn(formProps.errors, propName) && getIn(formProps.touched, propName),
      )}
    >
      <Flex flexDirection={flexDirection} alignItems={alignItems}>
        {label && (
          <FormFieldLabel
            label={label}
            sublabel={sublabel}
            labelWidth={labelWidth}
          />
        )}
        <InputGroup width="100%">
          <Textarea
            placeholder={placeholder}
            data-testid={propName}
            width={inputWidth}
            height={inputHeight}
            maxHeight={maxInputHeight}
            name={propName}
            value={value || getIn(formProps.values, propName) || ''}
            onChange={formProps.handleChange}
            onBlur={formProps.handleBlur}
            readOnly={readOnly}
            hidden={hidden}
            overflowY="scroll"
          />
        </InputGroup>
      </Flex>
      <FormErrorMessage>{getIn(formProps.errors, propName)}</FormErrorMessage>
    </FormControl>
  );
};

type TextInputFormFieldProps<K> = {
  name: K;
  type?: 'password' | 'email' | 'text';
  label: string;
  placeholder?: string;
  readOnly?: boolean;
  isDisabled?: boolean;
};

export const TextInputFormField = <
  T extends { [key: string]: string },
  K extends string & keyof T
>({
  name,
  type = 'text',
  label,
  placeholder = '',
  readOnly = false,
  isDisabled = false,
}: TextInputFormFieldProps<K>) => {
  const formik = useFormikContext<T>();
  return (
    <FormControl isInvalid={!!formik.errors[name]}>
      <FormLabel>{label}</FormLabel>
      <Input
        id={name}
        name={name}
        type={type}
        value={formik.values[name]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        placeholder={placeholder}
        readOnly={readOnly}
        isDisabled={isDisabled}
      />
      {formik.errors[name] && (
        <FormErrorMessage>
          {getSingleErrorMessageFromFieldErrors(formik.errors[name])}
        </FormErrorMessage>
      )}
    </FormControl>
  );
};

export const SelectFormField = <
  T extends { [key: string]: string },
  K extends string & keyof T
>({
  name,
  label,
  options,
  isDisabled = false,
  onChange,
  width,
}: {
  name: K;
  label: string;
  options: { label: string; value: string }[];
  isDisabled?: boolean;
  onChange?: (newValue: string) => void;
  width?: BoxProps['width'];
}) => {
  const formik = useFormikContext<T>();

  return (
    <FormControl isInvalid={!!formik.errors[name]}>
      <FormLabel>{label}</FormLabel>
      <FinleySelect
        value={getIn(formik.values, name)}
        options={options}
        onChange={(newValue) => {
          if (newValue) {
            formik.setFieldValue(name, newValue);
            onChange?.(newValue);
          }
        }}
        width={width ?? '100%'}
        isDisabled={isDisabled}
      />
      {formik.errors[name] && (
        <FormErrorMessage>
          {getSingleErrorMessageFromFieldErrors(formik.errors[name])}
        </FormErrorMessage>
      )}
    </FormControl>
  );
};

export const WhiteSubmitButton = (buttonProps: {
  isLoading?: boolean;
  label: React.ReactNode;
  rightIcon?: React.ReactElement;
  width?: string;
  height?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type?: 'button' | 'submit';
  isDisabled?: boolean;
}) => {
  const widthPx = buttonProps.width ? buttonProps.width : '115px';
  const heightPx = buttonProps.height ? buttonProps.height : '45px';
  const buttonType = buttonProps.type ? buttonProps.type : 'submit';

  return (
    <Button
      type={buttonType}
      height={heightPx}
      width={widthPx}
      border="1px"
      borderColor="#DBD3CA"
      bg="white"
      color="black"
      padding="16px 24px"
      fontSize="16px"
      fontWeight="light"
      borderRadius="0"
      iconSpacing="8px"
      _hover={{ bg: '#f9f6f1' }}
      {...buttonProps}
    >
      {buttonProps.label}
    </Button>
  );
};

export const FormSubmitButton = ({
  isSubmitting,
  width,
  children,
}: {
  isSubmitting: boolean;
  width: string;
  children: React.ReactNode;
}) => {
  const bgColor = useColorModeValue(colors.aurora['400'], 'white');
  const textColor = useColorModeValue('white', 'black');
  return (
    <Button
      marginTop={6}
      type="submit"
      backgroundColor={bgColor}
      fontWeight="500"
      color={textColor}
      variant="solid"
      width={width}
      isLoading={isSubmitting}
    >
      {children}
    </Button>
  );
};

// typeform input field that e.g. draw requests uses
export const TypeFormInputField = ({
  isRequired,
  isDisabled,
  handleChange,
  propName,
  formProps,
  children,
  value,
  showHideButton,
  type,
  label,
  sublabel,
  placeholder,
  inputWidth,
}: InputFormFieldProps & { children?: React.ReactNode }) => {
  const [show, setShow] = useState(!showHideButton);
  let inputType = type === 'dollar' ? 'number' : type;

  if (!show) {
    inputType = 'password';
  }

  let PlaceholderIcon;

  if (type === 'dollar') {
    PlaceholderIcon = (
      <InputLeftElement
        justifyContent="left"
        pointerEvents="none"
        color="black"
        height="100%"
        pb="0.5px"
      >
        $
      </InputLeftElement>
    );
  }

  const handleShowHideClick = () => setShow(!show);

  return (
    <FormControl
      marginTop={30}
      isRequired={isRequired}
      isInvalid={Boolean(
        formProps.errors[propName] && formProps.touched[propName],
      )}
    >
      {label && <FormFieldLabel label={label} sublabel={sublabel} />}
      <InputGroup>
        {PlaceholderIcon}
        <Input
          _focus={{ outline: 'none' }}
          _hover={{ borderBottom: '1px solid black' }}
          pl={PlaceholderIcon ? '18px' : '0'}
          border="none"
          borderRadius="none"
          borderBottom="1px solid black"
          boxShadow="none !important"
          type={inputType}
          placeholder={placeholder}
          width={inputWidth}
          name={propName}
          value={value || formProps.values[propName]}
          onChange={(e) => {
            formProps.handleChange(e);
            if (handleChange) {
              handleChange(e);
            }
          }}
          onBlur={formProps.handleBlur}
          isDisabled={isDisabled}
        />
        {showHideButton && (
          <InputRightElement width="4.5rem">
            <Button h="1.75rem" size="sm" onClick={handleShowHideClick}>
              {show ? 'Hide' : 'Show'}
            </Button>
          </InputRightElement>
        )}
      </InputGroup>
      <FormErrorMessage mt="0">
        {getSingleErrorMessageFromFieldErrors(formProps.errors[propName])}
      </FormErrorMessage>
      {children}
    </FormControl>
  );
};

// Form field for editing user info on Settings page
export const SettingsFormField = ({
  isRequired,
  handleChange,
  propName,
  formProps,
  children,
  label,
  placeholder,
}: InputFormFieldProps & { children?: React.ReactNode }) => {
  return (
    <FormControl
      isRequired={isRequired}
      isInvalid={Boolean(
        formProps.errors[propName] && formProps.touched[propName],
      )}
    >
      {label && (
        <Text fontSize="14px" color="#979797" fontWeight="bold" mb="5px">
          {label}
        </Text>
      )}

      <Input
        placeholder={placeholder}
        name={propName}
        value={formProps.values[propName]}
        onChange={(e) => {
          formProps.handleChange(e);
          if (handleChange) {
            handleChange(e);
          }
        }}
        onBlur={formProps.handleBlur}
      />
      <FormErrorMessage mt="0">
        {getSingleErrorMessageFromFieldErrors(formProps.errors[propName])}
      </FormErrorMessage>
      {children}
    </FormControl>
  );
};

export const SelectFormInput = ({
  isRequired,
  isDisabled,
  handleChange,
  propName,
  formProps,
  children,
  selectOptions,
  label,
  sublabel,
  marginTop, // 30
  placeholder,
}: InputFormFieldPropsWithSelectOptions & { children?: React.ReactNode }) => {
  return (
    <FormControl
      marginTop={marginTop}
      isRequired={isRequired}
      isInvalid={Boolean(
        formProps.errors[propName] && formProps.touched[propName],
      )}
    >
      {label && <FormFieldLabel label={label} sublabel={sublabel} />}
      <Select
        variant="flushed"
        border="none"
        borderRadius="none"
        borderBottom="1px solid black !important"
        boxShadow="none !important"
        _focus={{ outline: 'none' }}
        _hover={{ borderBottom: '1px solid black' }}
        placeholder={placeholder}
        name={propName}
        value={getIn(formProps.values, propName)}
        color={getIn(formProps.values, propName) === '' ? 'gray.400' : 'black'}
        onChange={(e) => {
          formProps.handleChange(e);
          if (handleChange) {
            handleChange(e);
          }
        }}
        isDisabled={isDisabled}
        onBlur={formProps.handleBlur}
      >
        {_.map(selectOptions, (option) => {
          return (
            <option key={option.value} value={option.value}>
              {option.label || option.value}
            </option>
          );
        })}
      </Select>
      <FormErrorMessage mt="0">
        {getSingleErrorMessageFromFieldErrors(formProps.errors[propName])}
      </FormErrorMessage>
      {children}
    </FormControl>
  );
};
